import React, { useEffect, useState } from 'react'
import { Modal, Select, Form, Button, Col, Row, message } from 'antd'
import styled from '@emotion/styled'
import { useLazyQuery } from '@apollo/client'
import { FETCH_MENUS, GET_LIGHTSPEED_ORDER_PROFILES } from './queries'
import {
  LightspeedBusiness,
  LightspeedMenu,
  LightspeedOrderProfile,
  LightspeedStore
} from './types'
import { MerchantLocation } from '@slerp/controls'
import { DeleteOutlined, CloseOutlined } from '@ant-design/icons'
import isEqual from 'lodash/isEqual'
import { StyledButton } from 'components/Integrations/styles'
import { useStoreIntegration } from './hooks/useStoreIntegration'
import { PartnerName } from '../../constants'

interface LocationSectionProps {
  isOpen: boolean
  onOk: () => void
  onCancel: () => void
  selectedBusiness: LightspeedBusiness
  stores: MerchantLocation[]
  connectedLocations: LightspeedStore[]
  selectedLocation: LightspeedStore | null
  onError: () => void
}

type FormValues = {
  storeId: string
  businessLocationId: string
  menuId: string
  delivery: string
  pickup: string
  orderAtTable: string
}

type PreconnectVariables = {
  storeId: string
  businessLocationId: string
  businessLocationName: string
}

const LocationModal = ({
  onCancel,
  onOk,
  isOpen,
  selectedBusiness,
  stores,
  connectedLocations,
  selectedLocation,
  onError
}: LocationSectionProps) => {
  const [form] = Form.useForm()

  const [canSubmit, setCanSubmit] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [lastConnectedSnapshot, setLastConnectedSnapshot] =
    useState<PreconnectVariables | null>(null)

  const [menusData, setMenusData] = useState<LightspeedMenu[]>([])
  const [orderProfilesData, setOrderProfilesData] = useState<
    LightspeedOrderProfile[]
  >([])

  const { disconnectStore, connectStore, syncMenu } = useStoreIntegration()

  const [
    fetchOrderProfiles,
    { loading: orderProfilesLoading, errors: orderProfilesErrors }
  ] = useLazyQuery<{
    getLightspeedOrderProfiles: LightspeedOrderProfile[]
  }>(GET_LIGHTSPEED_ORDER_PROFILES, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onCompleted: (data) => {
      setOrderProfilesData(data?.getLightspeedOrderProfiles || [])
    }
  })

  const [fetchMenus, { loading: menusLoading, errors: menusErrors }] =
    useLazyQuery<{ fetchPartnerMenus: LightspeedMenu[] }>(FETCH_MENUS, {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      onCompleted: (data) => {
        setMenusData(data?.fetchPartnerMenus || [])
      }
    })

  useEffect(() => {
    if (menusErrors || orderProfilesErrors) {
      onError()
    }
  }, [menusErrors, orderProfilesErrors])

  useEffect(() => {
    if (selectedLocation) {
      setIsLoading(true)

      const promises = [
        fetchOrderProfiles({
          variables: { storeId: selectedLocation.id }
        }),
        fetchMenus({
          variables: {
            partnerName: PartnerName.LIGHTSPEED,
            storeId: selectedLocation.id
          }
        })
      ]

      Promise.all(promises).then(() => {
        setIsLoading(false)
        setCanSubmit(true)
        form.setFieldsValue({
          storeId: selectedLocation?.id,
          businessLocationId:
            selectedLocation?.store_integration_settings
              .lightspeed_business_location_id,
          menuId:
            selectedLocation?.store_integration_settings.lightspeed_menus[0]
              ?.id,
          delivery:
            selectedLocation?.store_integration_settings
              .lightspeed_order_profile_mapping?.delivery || null,
          pickup:
            selectedLocation?.store_integration_settings
              .lightspeed_order_profile_mapping?.pickup || null,
          orderAtTable:
            selectedLocation?.store_integration_settings
              .lightspeed_order_profile_mapping?.orderAtTable || null
        })
      })
    }
  }, [selectedLocation])

  const preconnectStore = async (variables: PreconnectVariables | null) => {
    if (isEqual(variables, lastConnectedSnapshot)) {
      return
    }

    const shouldDisconnect =
      lastConnectedSnapshot &&
      !connectedLocations.some(
        ({
          id,
          store_integration_settings: { lightspeed_business_location_id }
        }) =>
          id === lastConnectedSnapshot?.storeId &&
          lightspeed_business_location_id ===
            lastConnectedSnapshot?.businessLocationId
      )

    if (shouldDisconnect) {
      await disconnectStore({
        storeId: lastConnectedSnapshot.storeId
      })
    }

    const shouldConnect =
      variables &&
      !connectedLocations.some(
        ({
          id,
          store_integration_settings: { lightspeed_business_location_id }
        }) =>
          id === variables?.storeId &&
          lightspeed_business_location_id === variables?.businessLocationId
      )

    if (shouldConnect) {
      await connectStore({
        storeId: variables.storeId,
        businessId: selectedBusiness.id,
        businessName: selectedBusiness.name,
        businessLocationId: variables.businessLocationId,
        businessLocationName: variables.businessLocationName
      })

      setLastConnectedSnapshot(variables)
    }

    if (!variables) {
      setLastConnectedSnapshot(null)
    }
  }

  const handleChange = async (changedFields: { name: string }[]) => {
    const storeId = form.getFieldValue('storeId')
    const businessLocationId = form.getFieldValue('businessLocationId')
    const menuId = form.getFieldValue('menuId')

    setCanSubmit(Boolean(storeId && businessLocationId && menuId))

    if (!['storeId', 'businessLocationId'].includes(changedFields[0].name[0])) {
      return
    }

    const businessLocationName = selectedBusiness.businessLocations.find(
      (location) => location.id === businessLocationId
    )?.name

    if (!storeId || !businessLocationId || !businessLocationName) {
      return
    }

    await preconnectStore({
      storeId,
      businessLocationId,
      businessLocationName
    })

    fetchMenus({
      variables: { partnerName: PartnerName.LIGHTSPEED, storeId }
    })

    fetchOrderProfiles({
      variables: { storeId }
    })
  }

  const handleFinish = async ({
    storeId,
    businessLocationId,
    menuId,
    delivery,
    pickup,
    orderAtTable
  }: FormValues) => {
    const businessLocationName = selectedBusiness.businessLocations.find(
      (location) => location.id === businessLocationId
    )?.name

    const menuName = menusData.find((menu) => menu.id === menuId)?.name

    if (!businessLocationName || !menuName) {
      return
    }

    if (selectedLocation && selectedLocation.id !== storeId) {
      disconnectStore({ storeId: selectedLocation.id })
    }

    try {
      await connectStore({
        storeId: storeId,
        businessId: selectedBusiness.id,
        businessName: selectedBusiness.name,
        businessLocationId: businessLocationId,
        businessLocationName: businessLocationName,
        menus: [{ id: menuId, name: menuName }],
        orderProfileMapping: {
          delivery,
          pickup,
          orderAtTable
        }
      })

      message.destroy()
      message.success('Store connected successfully!')

      onOk()
      cleanUp(true)
    } catch (error) {
      message.destroy()
      message.error('Failed to connect store!')

      onError()
    }
  }

  const handleRemove = async () => {
    if (!selectedLocation?.id) {
      return
    }

    try {
      await disconnectStore({ storeId: selectedLocation.id })
      message.destroy()
      message.success('Connection removed successfully!')

      onOk()
      cleanUp()
    } catch (error) {
      message.destroy()
      message.error('Failed to remove connection!')

      onError()
    }
  }

  const handleCancel = () => {
    cleanUp()
    onCancel()
  }

  const handleSyncMenu = async () => {
    if (!selectedLocation?.id) {
      return
    }

    try {
      await syncMenu(selectedLocation.id)
      message.destroy()
      message.success('Menu synced successfully!')

      cleanUp()
      onOk()
    } catch (error) {
      message.destroy()
      message.error('Failed to sync menu!')

      onError()
    }
  }

  const cleanUp = (isSuccess = false) => {
    setCanSubmit(false)
    setMenusData([])
    setOrderProfilesData([])
    form.resetFields()

    if (isSuccess) {
      setLastConnectedSnapshot(null)
      return
    }

    preconnectStore(null)
  }

  const slerpStoreOptions = stores.map((store) => ({
    label: store.name,
    value: store.id
  }))

  const lightspeedLocationOptions = selectedBusiness.businessLocations.map(
    (location) => ({
      label: location.name,
      value: location.id
    })
  )

  const lightspeedMenuOptions = menusData.map((menu) => ({
    label: menu.name,
    value: menu.id
  }))

  const lightspeedOrderProfileMapped = orderProfilesData.map((profile) => ({
    label: `${profile.code[0].toUpperCase()}${profile.code.slice(1)}`,
    value: profile.code
  }))

  const lightspeedOrderProfileOptions = [
    { label: 'Disabled', value: null },
    ...lightspeedOrderProfileMapped
  ]

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      closable={false}
      footer={false}
    >
      <ModalHeader>
        <ModalTitle>Connect Stores and Menus</ModalTitle>
        <ModalDescription>
          Map your Slerp store to a Lightspeed store. After mapping is complete,
          choose the Lightspeed menu you want to pull into your Slerp store
          inventory.
        </ModalDescription>

        <CloseButton type='text' onClick={handleCancel}>
          <CloseOutlined />
        </CloseButton>
      </ModalHeader>

      <Form
        onFinish={handleFinish}
        onFieldsChange={handleChange}
        form={form}
        initialValues={{
          delivery: null,
          pickup: null,
          orderAtTable: null
        }}
      >
        <Card isLoading={isLoading}>
          <div>
            <FormLabel>
              <FormItemTitle>Slerp Store</FormItemTitle>
              <FormItemDescription>
                Choose the store to connect to Lightspeed.
              </FormItemDescription>
            </FormLabel>
            <FormItem name='storeId' required>
              <Select
                placeholder='Select Slerp Store'
                options={slerpStoreOptions}
              />
            </FormItem>
          </div>

          <div>
            <FormLabel>
              <FormItemTitle>Lightspeed Location</FormItemTitle>
              <FormItemDescription>
                Changing the Lightspeed store will require you to reselect a
                menu.
              </FormItemDescription>
            </FormLabel>
            <FormItem name='businessLocationId' required>
              <Select
                placeholder='Select Lightspeed Location'
                options={lightspeedLocationOptions}
              />
            </FormItem>
          </div>

          {!!selectedLocation && (
            <RemoveButton type='text' onClick={handleRemove}>
              <span>Delete Connection</span>
              <DeleteOutlined />
            </RemoveButton>
          )}
        </Card>

        <Card gap={8} isLoading={orderProfilesLoading}>
          <div>
            <FormLabel>
              <FormItemTitle>Enable fulfilment types</FormItemTitle>
              <FormItemDescription>
                To sync a fulfillment type with Lightspeed, map it to an Order
                Profile. Unmapped types won’t be sent to the POS.
              </FormItemDescription>
            </FormLabel>
          </div>

          <div>
            <StyledRow>
              <Col span={12}>
                <FormItemTitle>Slerp Fulfiment type</FormItemTitle>
              </Col>
              <Col span={12}>
                <FormItemTitle>Lightspeed order profile</FormItemTitle>
              </Col>
            </StyledRow>
            <StyledRow align='middle'>
              <Col span={12}>
                <FormItemTitle>Delivery</FormItemTitle>
              </Col>
              <Col span={12}>
                <FormItem name='delivery'>
                  <Select
                    options={lightspeedOrderProfileOptions}
                    placeholder='Select Delivery Profile'
                  />
                </FormItem>
              </Col>
            </StyledRow>
            <StyledRow align='middle'>
              <Col span={12}>
                <FormItemTitle>Pickup</FormItemTitle>
              </Col>
              <Col span={12}>
                <FormItem name='pickup'>
                  <Select
                    options={lightspeedOrderProfileOptions}
                    placeholder='Select Pickup Profile'
                  />
                </FormItem>
              </Col>
            </StyledRow>
            <StyledRow align='middle'>
              <Col span={12}>
                <FormItemTitle>Order at table</FormItemTitle>
              </Col>
              <Col span={12}>
                <FormItem name='orderAtTable'>
                  <Select
                    options={lightspeedOrderProfileOptions}
                    placeholder='Select Table Order Profile'
                  />
                </FormItem>
              </Col>
            </StyledRow>
          </div>
        </Card>

        <Card isLoading={menusLoading}>
          <div>
            <FormLabel>
              <FormItemTitle>Select Menu</FormItemTitle>
              <FormItemDescription>
                This menu will be used as your Slerp inventory.
              </FormItemDescription>
            </FormLabel>
            <FormItem name='menuId' required>
              <Select
                placeholder='Select Lightspeed Menu'
                options={lightspeedMenuOptions}
              />
            </FormItem>
          </div>
        </Card>

        <Actions>
          {!!selectedLocation && (
            <StyledButton onClick={handleSyncMenu} disabled={!canSubmit}>
              <span>Sync menu</span>
            </StyledButton>
          )}

          <ModalButton htmlType='submit' disabled={!canSubmit}>
            {selectedLocation ? 'Update' : 'Save'}
          </ModalButton>
        </Actions>
      </Form>
    </Modal>
  )
}

const StyledRow = styled(Row)`
  margin-bottom: 6px;
`

const CloseButton = styled(Button)`
  position: absolute;
  top: -1px;
  right: -12px;

  background: transparent;
  border: none;
  color: #4a4a68;

  &:hover,
  &:focus,
  &:active {
    color: #4a4a68;
    background: transparent;
    border: none;
  }
`

const ModalHeader = styled.div`
  position: relative;
`

const Actions = styled.div`
  display: flex;
  gap: 24px;

  > * {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const RemoveButton = styled(Button)`
  position: absolute;
  top: 3px;
  right: 0;

  display: flex;
  align-items: center;

  font-size: 12px;
  font-weight: 400;
  text-transform: capitalize !important;
  color: #a40000;
  border: none;

  &:hover,
  &:focus,
  &:active {
    color: #a40000;
    background: transparent;
    border: none;
  }
`

const ModalTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #4a4a68;
`

const ModalDescription = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: #4a4a68;
`

const Card = styled.div<{ isLoading?: boolean; gap?: number }>(
  ({ isLoading, gap = 16 }) => `
  position: relative;

  display: flex;
  flex-direction: column;
  gap: ${gap}px;

  padding: 12px;
  border: 1px solid #a4a4b3;
  border-radius: 4px;
  margin-bottom: 24px;

  ${
    isLoading &&
    `
      .ant-select-selector {
        position: relative;
        z-index: 0;

        &::after{
          content:'';
          position: absolute;
          left:0;
          top:0;
          border-radius: 5px;
          display: inline-flex;
          visibility:visible;
          width: 100%;
          height: 100%;
          z-index: 3;
          background-size: 200% 100%;
          background-image: linear-gradient(90deg, #f8f8f8 0, #ededed 10%, #f8f8f8 15%, #f8f8f8 55%, #ededed 65%, #f8f8f8 70%);
          animation: shimmer 3s linear infinite
        }
      }

      @keyframes shimmer {
        0% {
          background-position: 100%;
        }
        100% {
          background-position: -100%;
        }
      }
    `
  }
`
)

const FormItem = styled(Form.Item)`
  margin-bottom: 0;
`

const FormLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
`

const FormItemTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #4a4a68;
  line-height: 1;
`

const FormItemDescription = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #77778e;
  line-height: 1.2;
`

const ModalButton = styled(Button)`
  width: 100%;
`

export default LocationModal
